import { infiniteQueryOptions, useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { listFiles, renameFile, softDeleteFile } from "../api/files.api";
import { listFolders, createFolder, renameFolder, softDeleteFolder } from "../api/folders.api";
import { useExplorerStore } from "../store/explorerStore";
import { FOLDER_INDEX_KEY, useFolderIndex } from "./useFolderIndex";
import type { ExplorerItem, FileDTO, FolderDTO, FolderIndexDTO, SortField, SortOrder } from "@titancloud/shared";

const PAGE_SIZE = 50;

/** Shared by the explorer and hover prefetch so both read and fill the same cache entry. */
export function filesQueryOptions(folderId: string | null, sort: SortField, order: SortOrder) {
  return infiniteQueryOptions({
    queryKey: ["files", folderId, sort, order] as const,
    queryFn: ({ pageParam }) =>
      listFiles({ folderId, sort, order, limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30 * 1000,
  });
}

export function useFileExplorer() {
  const { currentFolderId, sort, order } = useExplorerStore();
  const qc = useQueryClient();

  const filesOptions = filesQueryOptions(currentFolderId, sort, order);
  const filesQueryKey = filesOptions.queryKey;
  const foldersQueryKey = ["folders", currentFolderId] as const;

  // Subfolders come from the cached folder tree; the per-folder request is only a fallback for
  // when the tree is unavailable or does not know this folder yet (e.g. created on another device).
  const folderIndex = useFolderIndex();
  const indexKnowsFolder =
    folderIndex.index !== null && (currentFolderId === null || folderIndex.index.byId.has(currentFolderId));

  const foldersQuery = useQuery({
    queryKey: foldersQueryKey,
    queryFn: () => listFolders(currentFolderId),
    enabled: !folderIndex.isPending && !indexKnowsFolder,
  });

  const folders: FolderDTO[] = indexKnowsFolder
    ? (folderIndex.index!.children.get(currentFolderId) ?? [])
    : (foldersQuery.data ?? []);
  const isLoadingFolders = folderIndex.isPending || foldersQuery.isLoading;

  const filesQuery = useInfiniteQuery(filesOptions);

  const allFiles: FileDTO[] = filesQuery.data?.pages.flatMap((p) => p.files) ?? [];
  const totalFilesCount: number = filesQuery.data?.pages[0]?.total ?? 0;

  const allItems: ExplorerItem[] = [
    ...folders.map((f): ExplorerItem => ({
      ...f,
      kind: "folder" as const,
      isDeleted: f.isDeleted ?? false,
      deletedAt: f.deletedAt ?? null,
    })),
    ...allFiles.map((f): ExplorerItem => ({
      ...f,
      kind: "file" as const,
    })),
  ];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["files", currentFolderId] });
    void qc.invalidateQueries({ queryKey: foldersQueryKey });
    void qc.invalidateQueries({ queryKey: FOLDER_INDEX_KEY });
  };

  const createFolderMutation = useMutation({
    mutationFn: ({ name }: { name: string }) =>
      createFolder(name, currentFolderId),
    onSuccess: (_data, vars) => {
      invalidate();
      toast.success(`Folder "${vars.name}" created`);
    },
    onError: () => toast.error("Failed to create folder"),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name, kind }: { id: string; name: string; kind: "file" | "folder" }): Promise<FileDTO | FolderDTO> =>
      kind === "file" ? renameFile(id, name) : renameFolder(id, name),
    onSuccess: invalidate,
    onError: () => toast.error("Rename failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: "file" | "folder" }) =>
      kind === "file" ? softDeleteFile(id) : softDeleteFolder(id),
    onMutate: async ({ id, kind }) => {
      // Cancel any in-flight refetches so they don't clobber our optimistic update
      await qc.cancelQueries({ queryKey: filesQueryKey });
      await qc.cancelQueries({ queryKey: foldersQueryKey });
      await qc.cancelQueries({ queryKey: FOLDER_INDEX_KEY });

      // Snapshot current cache for rollback
      const prevFiles = qc.getQueryData(filesQueryKey);
      const prevFolders = qc.getQueryData(foldersQueryKey);
      const prevIndex = qc.getQueryData<FolderIndexDTO>(FOLDER_INDEX_KEY);

      // Optimistically remove the item from the cache immediately
      if (kind === "file") {
        qc.setQueryData(filesQueryKey, (old: typeof filesQuery.data) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              files: page.files.filter((f) => f.id !== id),
              total: Math.max(0, page.total - 1),
            })),
          };
        });
      } else {
        qc.setQueryData(foldersQueryKey, (old: FolderDTO[] | undefined) =>
          old?.filter((f) => f.id !== id)
        );
        qc.setQueryData(FOLDER_INDEX_KEY, (old: FolderIndexDTO | undefined) =>
          old && { ...old, folders: old.folders.filter((f) => f.id !== id) }
        );
      }

      return { prevFiles, prevFolders, prevIndex };
    },
    onError: (_err, vars, context) => {
      // Roll back to the snapshot
      if (context?.prevFiles) qc.setQueryData(filesQueryKey, context.prevFiles);
      if (context?.prevFolders) qc.setQueryData(foldersQueryKey, context.prevFolders);
      if (context?.prevIndex) qc.setQueryData(FOLDER_INDEX_KEY, context.prevIndex);
      toast.error("Delete failed");
    },
    onSuccess: (_data, vars) => {
      toast.success(vars.kind === "folder" ? "Folder moved to trash" : "File moved to trash");
    },
    onSettled: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ["trash"] });
    },
  });

  return {
    allItems,
    totalCount: folders.length + totalFilesCount,
    loadedFilesCount: allFiles.length,
    totalFilesCount,
    hasNextPage: filesQuery.hasNextPage,
    fetchNextPage: filesQuery.fetchNextPage,
    isFetchingNextPage: filesQuery.isFetchingNextPage,
    // Known subfolders render straight away while files load; the full skeleton is only for
    // when there is nothing to show yet, so an empty folder never flashes "This folder is empty".
    isLoading: isLoadingFolders || (filesQuery.isLoading && folders.length === 0),
    isLoadingFiles: filesQuery.isLoading,
    error: foldersQuery.error ?? filesQuery.error,
    createFolder: createFolderMutation.mutate,
    isCreatingFolder: createFolderMutation.isPending,
    creatingFolderName: createFolderMutation.variables?.name ?? "",
    rename: renameMutation.mutate,
    delete: deleteMutation.mutate,
    invalidate,
  };
}
